import { useEffect } from "react";

/**
 * Update document <title>, <meta description>, and OG/JSON-LD tags on route change.
 * Client-side SEO — works for social sharing crawlers that render JS (Twitter, LinkedIn)
 * and for indexing since document.title is set on load.
 */
export default function useSEO({
  title,
  description,
  image,
  url,
  type = "website",
  jsonLd = null,
  noindex = false,
} = {}) {
  useEffect(() => {
    const previousTitle = document.title;
    if (title) document.title = title;

    const upsertMeta = (attr, key, value) => {
      if (!value) return null;
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", value);
      return el;
    };

    upsertMeta("name", "description", description);
    upsertMeta("name", "robots", noindex ? "noindex,nofollow" : "index,follow");
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:image", image);
    upsertMeta("property", "og:url", url);
    upsertMeta("name", "twitter:card", image ? "summary_large_image" : "summary");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", image);

    // Canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (url) {
      if (!canonical) {
        canonical = document.createElement("link");
        canonical.setAttribute("rel", "canonical");
        document.head.appendChild(canonical);
      }
      canonical.setAttribute("href", url);
    }

    // JSON-LD
    let ldTag = document.getElementById("seo-jsonld");
    if (jsonLd) {
      if (!ldTag) {
        ldTag = document.createElement("script");
        ldTag.type = "application/ld+json";
        ldTag.id = "seo-jsonld";
        document.head.appendChild(ldTag);
      }
      ldTag.textContent = JSON.stringify(jsonLd);
    } else if (ldTag) {
      ldTag.remove();
    }

    return () => {
      document.title = previousTitle;
    };
  }, [title, description, image, url, type, noindex, JSON.stringify(jsonLd)]);
}
